import { API_BASE } from '../api/index.js';

// Configuration
const CONFIG = {
  ACCESS_TOKEN_LIFETIME: 8 * 60 * 60 * 1000,  // 8 hours
  REFRESH_INTERVAL: 7 * 60 * 60 * 1000,        // 7 hours (1 hour before expiry refresh)
  INACTIVITY_TIMEOUT: 15 * 60 * 1000,          // 15 minutes inactivity logout
  ACTIVITY_CHECK_INTERVAL: 30 * 1000,          // unchanged
  REFRESH_RETRY_ATTEMPTS: 3,                   // unchanged
  REFRESH_RETRY_DELAY: 2000,                   // unchanged
};

// Activity tracking
let lastActivityTime = Date.now();
let activityCheckInterval = null;
let tokenRefreshInterval = null;
let isLoggingOut = false;

//  FIX: Store bound function reference for proper cleanup
let boundUpdateActivity = null;

// ============================================
// TOKEN MANAGEMENT
// ============================================

export const getAccessToken = () => localStorage.getItem('accessToken');
export const getRefreshToken = () => localStorage.getItem('refreshToken');

// 👇 UPDATED: Safely handle allowedModels extraction
export const getUser = () => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return null;
  
  try {
    const parsedUser = JSON.parse(userStr);
    
    // SAFETY CHECK: Ensure allowedModels always exists as an array 
    if (parsedUser && !Array.isArray(parsedUser.allowedModels)) {
      parsedUser.allowedModels = [];
    }
    
    return parsedUser;
  } catch (error) {
    console.error('Failed to parse user from localStorage', error);
    return null;
  }
};

export const setTokens = (accessToken, refreshToken) => {
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
};

// 👇 UPDATED: Safely save allowedModels
export const setUser = (user) => {
  if (user) {
    if (!Array.isArray(user.allowedModels)) {
      user.allowedModels = [];
    }
    localStorage.setItem('user', JSON.stringify(user));
  } else {
    localStorage.removeItem('user');
  }
};

export const clearAuth = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  stopActivityTracking();
  stopTokenRefresh();
};

// ============================================
// ACTIVITY TRACKING (FIXED)
// ============================================

// Update last activity time
const updateActivity = () => {
  //  FIX: Check if we're logged out before processing activity
  if (isLoggingOut || !getAccessToken()) {
    console.log(' [ACTIVITY] Ignoring activity - user logged out');
    return;
  }
  
  const previousActivity = lastActivityTime;
  lastActivityTime = Date.now();
  const timeSinceLastActivity = Math.floor((lastActivityTime - previousActivity) / 1000);
  
  console.log(' [ACTIVITY] Activity detected at:', new Date().toLocaleTimeString());
  console.log(` [ACTIVITY] Time since last activity: ${timeSinceLastActivity} seconds`);
};

// Start tracking user activity
export const startActivityTracking = () => {
  console.log(' [ACTIVITY TRACKING] Starting activity tracker...');
  
  // Stop any existing tracker first
  stopActivityTracking();
  
  //  FIX: Create bound function reference
  boundUpdateActivity = updateActivity.bind(null);
  
  // Track various user interactions
  const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'mousemove'];
  
  console.log(` [ACTIVITY TRACKING] Registering ${activityEvents.length} event listeners...`);
  
  activityEvents.forEach(event => {
    //  FIX: Use the bound reference
    document.addEventListener(event, boundUpdateActivity, { passive: true });
    console.log(` [ACTIVITY TRACKING] Registered: ${event}`);
  });

  // Update activity immediately
  updateActivity();

  // Check for inactivity every 30 seconds
  activityCheckInterval = setInterval(() => {
    //  FIX: Double-check we're still authenticated
    if (isLoggingOut || !getAccessToken()) {
      console.log(' [INACTIVITY CHECK] User logged out, stopping checks');
      stopActivityTracking();
      return;
    }
    
    const inactiveTime = Date.now() - lastActivityTime;
    const inactiveMinutes = Math.floor(inactiveTime / 60000);
    const inactiveSeconds = Math.floor((inactiveTime % 60000) / 1000);
    const remainingTime = CONFIG.INACTIVITY_TIMEOUT - inactiveTime;
    const remainingMinutes = Math.max(0, Math.floor(remainingTime / 60000));
    
    // Detailed logging
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log(` [INACTIVITY CHECK] Inactive for: ${inactiveMinutes} min ${inactiveSeconds} sec`);
    console.log(` [INACTIVITY CHECK] Last activity: ${new Date(lastActivityTime).toLocaleTimeString()}`);
    console.log(` [INACTIVITY CHECK] Will logout in: ${remainingMinutes} minutes`);
    console.log(` [INACTIVITY CHECK] Interval active: ${!!activityCheckInterval}`);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    
    // If inactive for more than configured timeout, logout
    if (inactiveTime >= CONFIG.INACTIVITY_TIMEOUT) {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log(' [AUTO-LOGOUT] 15 minutes of inactivity detected!');
      console.log(' [AUTO-LOGOUT] Logging out user...');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      // alert('You have been logged out due to inactivity. Please login again.');
      logout('inactivity');
    }
  }, CONFIG.ACTIVITY_CHECK_INTERVAL);
  
  console.log(' [ACTIVITY TRACKING] Activity tracking started successfully!');
  console.log(` [ACTIVITY TRACKING] Checking inactivity every ${CONFIG.ACTIVITY_CHECK_INTERVAL / 1000} seconds`);
  console.log(` [ACTIVITY TRACKING] Auto-logout after ${CONFIG.INACTIVITY_TIMEOUT / (60 * 60 * 1000)} hours of inactivity`);
};

// 🔥 FIX: Improved stopActivityTracking with proper cleanup
const stopActivityTracking = () => {
  console.log(' [ACTIVITY TRACKING] Stopping activity tracker...');
  
  // 🔥 FIX: Remove event listeners using the same bound reference
  if (boundUpdateActivity) {
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'mousemove'];
    activityEvents.forEach(event => {
      document.removeEventListener(event, boundUpdateActivity, { passive: true });
      console.log(` [ACTIVITY TRACKING] Removed listener: ${event}`);
    });
    boundUpdateActivity = null; // Clear reference
  }
  
  // Clear interval
  if (activityCheckInterval) {
    clearInterval(activityCheckInterval);
    activityCheckInterval = null;
    console.log(' [ACTIVITY TRACKING] Activity tracker stopped');
  }
};

// ============================================
// API CALLS WITH AUTO TOKEN REFRESH
// ============================================

export const apiCall = async (url, options = {}) => {
  const accessToken = getAccessToken();
  
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      ...options.headers,
    },
  };

  try {
    const response = await fetch(`${API_BASE}${url}`, config);
    
    // If token expired (401), try to refresh with retry logic
    if (response.status === 401 && accessToken) {
      console.log('🔄 [TOKEN] Access token expired, attempting refresh with retries...');
      const refreshed = await refreshAccessToken();
      
      if (refreshed) {
        // Retry request with new token
        console.log(' [TOKEN] Retrying original API call with new token');
        config.headers.Authorization = `Bearer ${getAccessToken()}`;
        return await fetch(`${API_BASE}${url}`, config);
      } else {
        // Refresh failed after all retries - check if token is truly invalid
        const stillHasRefreshToken = !!getRefreshToken();
        
        if (!stillHasRefreshToken) {
          console.error(' [TOKEN] No refresh token available, logging out');
          await logout('token-expired');
        } else {
          console.error(' [TOKEN] Token refresh failed after retries, logging out');
          await logout('token-refresh-failed');
        }
        return null;
      }
    }
    
    return response;
  } catch (error) {
    console.error(' [API] API call failed:', error);
    throw error;
  }
};

// ============================================
// TOKEN REFRESH (WITH RETRY LOGIC)
// ============================================

export const refreshAccessToken = async (retryCount = 0) => {
  const refreshToken = getRefreshToken();
  
  if (!refreshToken) {
    console.error(' [TOKEN] No refresh token found');
    return false;
  }

  try {
    console.log(` [TOKEN] Refreshing access token... (Attempt ${retryCount + 1}/${CONFIG.REFRESH_RETRY_ATTEMPTS})`);
    
    const response = await fetch(`${API_BASE}/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (response.ok) {
      const data = await response.json();
      
      // Update only access token (refresh token stays same)
      localStorage.setItem('accessToken', data.data.accessToken);
      
      console.log(' [TOKEN] Token refreshed successfully at', new Date().toLocaleTimeString());
      return true;
    } else if (response.status === 401 || response.status === 403) {
      // Token is truly invalid - don't retry
      console.error(' [TOKEN] Refresh token is invalid or expired (status:', response.status, ')');
      return false;
    } else {
      // Server error or network issue - retry if attempts remaining
      console.warn(` [TOKEN] Token refresh failed with status: ${response.status}`);
      
      if (retryCount < CONFIG.REFRESH_RETRY_ATTEMPTS - 1) {
        console.log(` [TOKEN] Retrying in ${CONFIG.REFRESH_RETRY_DELAY / 1000} seconds...`);
        await new Promise(resolve => setTimeout(resolve, CONFIG.REFRESH_RETRY_DELAY));
        return await refreshAccessToken(retryCount + 1);
      }
      
      console.error(' [TOKEN] All retry attempts exhausted');
      return false;
    }
  } catch (error) {
    // Network error - retry if attempts remaining
    console.error(` [TOKEN] Token refresh error (attempt ${retryCount + 1}):`, error.message);
    
    if (retryCount < CONFIG.REFRESH_RETRY_ATTEMPTS - 1) {
      console.log(` [TOKEN] Retrying in ${CONFIG.REFRESH_RETRY_DELAY / 1000} seconds...`);
      await new Promise(resolve => setTimeout(resolve, CONFIG.REFRESH_RETRY_DELAY));
      return await refreshAccessToken(retryCount + 1);
    }
    
    console.error(' [TOKEN] All retry attempts exhausted due to network errors');
    return false;
  }
};

// ============================================
// AUTO TOKEN REFRESH SETUP
// ============================================

export const setupTokenRefresh = () => {
  const accessToken = getAccessToken();
  
  if (!accessToken) {
    console.log(' [TOKEN] No access token, skipping auto-refresh setup');
    return;
  }

  console.log(` Setting up automatic token refresh (every ${CONFIG.REFRESH_INTERVAL / 60000} minutes)`);
  
  // Clear any existing interval
  stopTokenRefresh();
  
  // Refresh token every 11 minutes (4 minutes before 15-min expiry - safer buffer)
  tokenRefreshInterval = setInterval(async () => {
    //  Check if still authenticated before refreshing
    if (isLoggingOut || !getAccessToken()) {
      console.log(' [TOKEN] User logged out, stopping auto-refresh');
      stopTokenRefresh();
      return;
    }
    
    const currentToken = getAccessToken();
    
    if (currentToken) {
      console.log(' [TOKEN] Starting automatic token refresh...');
      const refreshed = await refreshAccessToken();
      
      if (!refreshed) {
        console.error(' [TOKEN] Auto-refresh failed after all retries');
        // Only logout if refresh token is truly invalid, not on network errors
        const stillHasRefreshToken = !!getRefreshToken();
        if (stillHasRefreshToken) {
          console.warn(' [TOKEN] Refresh token still exists - may be temporary network issue');
          console.warn(' [TOKEN] Will retry on next interval or when API call fails');
        } else {
          console.error(' [TOKEN] No refresh token - logging out');
          await logout('refresh-failed');
        }
      }
    } else {
      console.log(' [TOKEN] No token found, stopping auto-refresh');
      stopTokenRefresh();
    }
  }, CONFIG.REFRESH_INTERVAL);
  
  console.log(' Auto token refresh enabled');
};

// Stop token refresh
const stopTokenRefresh = () => {
  if (tokenRefreshInterval) {
    clearInterval(tokenRefreshInterval);
    tokenRefreshInterval = null;
    console.log(' Auto token refresh stopped');
  }
};

// ============================================
// LOGOUT (FIXED - Proper Cleanup)
// ============================================

export const logout = async (reason = 'manual') => {
  //  FIX: Prevent double logout
  if (isLoggingOut) {
    console.log(' [LOGOUT] Already logging out, skipping...');
    return;
  }
  
  //  FIX: Set flag IMMEDIATELY to stop all activity
  isLoggingOut = true;
  
  const refreshToken = getRefreshToken();
  
  console.log(' [LOGOUT] Logging out...');
  console.log(` [LOGOUT] Reason: ${reason}`);
  
  //  FIX: Stop all tracking BEFORE API call
  console.log('🛑 [LOGOUT] Stopping all services...');
  stopActivityTracking();
  stopTokenRefresh();
  
  try {
    // Call logout API to revoke session in database
    if (refreshToken) {
      await fetch(`${API_BASE}/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          refreshToken,
          reason
        }),
      });
      console.log('✅ [LOGOUT] Session revoked on server');
    }
  } catch (error) {
    console.error('❌ [LOGOUT] Logout API error:', error);
    // Continue with local cleanup even if API fails
  } finally {
    // Clear all auth data
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    
    console.log('✅ [LOGOUT] Logged out successfully');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    
    // 🔥 FIX: Reset flag before redirect
    // Don't reset here - let page reload handle it
    
    // Redirect to login page
    window.location.href = '/login';
  }
};

// ============================================
// AUTHENTICATION STATUS
// ============================================

export const isAuthenticated = () => {
  const hasToken = !!getAccessToken();
  const hasUser = !!getUser();
  return hasToken && hasUser && !isLoggingOut; // 🔥 FIX: Also check logout flag
};

// ============================================
// INITIALIZE AUTH (Call on app startup)
// ============================================

export const initializeAuth = () => {
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('🚀 [INIT] Initializing authentication service...');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  
  // 🔥 FIX: Reset logout flag on initialization
  isLoggingOut = false;
  
  if (isAuthenticated()) {
    console.log('✅ [INIT] User is authenticated, setting up services...');
    
    // Start automatic token refresh
    console.log('⚙️ [INIT] Starting token refresh service...');
    setupTokenRefresh();
    
    // Start activity tracking for inactivity timeout
    console.log('⚙️ [INIT] Starting activity tracking service...');
    startActivityTracking();
    
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('✅ [INIT] Auth services initialized successfully');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  } else {
    console.log('⚠️ [INIT] User not authenticated, skipping initialization');
  }
};

// ============================================
// DEBUG FUNCTION
// ============================================

export const getDebugInfo = () => {
  const inactiveTime = Date.now() - lastActivityTime;
  const inactiveMinutes = Math.floor(inactiveTime / 60000);
  const inactiveSeconds = Math.floor((inactiveTime % 60000) / 1000);
  const remainingTime = CONFIG.INACTIVITY_TIMEOUT - inactiveTime;
  const remainingMinutes = Math.max(0, Math.floor(remainingTime / 60000));
  
  return {
    authenticated: isAuthenticated(),
    lastActivity: new Date(lastActivityTime).toLocaleTimeString(),
    inactiveFor: `${inactiveMinutes}m ${inactiveSeconds}s`,
    willLogoutIn: `${remainingMinutes} minutes`,
    activityTrackingActive: !!activityCheckInterval,
    tokenRefreshActive: !!tokenRefreshInterval,
    hasAccessToken: !!getAccessToken(),
    hasRefreshToken: !!getRefreshToken(),
    hasUser: !!getUser(),
    isLoggingOut,
    boundListenerExists: !!boundUpdateActivity,
  };
};

// ============================================
// EXPORTS
// ============================================

// 👇 UPDATED: Assign to a constant before exporting to resolve the ESLint warning
const authService = {
  // Token management
  getAccessToken,
  getRefreshToken,
  getUser,
  setTokens,
  setUser,
  clearAuth,
  
  // API calls
  apiCall,
  
  // Token refresh
  refreshAccessToken,
  setupTokenRefresh,
  
  // Activity tracking
  startActivityTracking,
  updateActivity,
  
  // Auth status
  isAuthenticated,
  
  // Initialization
  initializeAuth,
  
  // Logout
  logout,
  
  // Debug
  getDebugInfo,
  
  // Configuration
  CONFIG,
};

export default authService;